import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { captureMessage } from '@/lib/sentry';

/**
 * Hook to generate and cache signed URLs for Supabase storage files.
 * Uses React Query to cache signed URLs by storage path, preventing
 * duplicate network requests for the same file.
 *
 * Skips requests when there is no active session to avoid false
 * "Object not found" errors caused by expired JWTs.
 *
 * @param storagePath - The storage path to the file (e.g., "org-id/images/logo.jpg")
 * @returns Query result with signedUrl, loading, and error states
 */
export function useSignedUrl(storagePath: string | null | undefined) {
  const { hasSession } = useAuth();

  return useQuery({
    queryKey: ['signed-url', storagePath],
    queryFn: async () => {
      if (!storagePath) return null;

      // hasSession is React state and can lag the real auth state (stale
      // localStorage session at boot, refetch racing a SIGNED_OUT re-render).
      // Without a session the sign request is RLS-denied and storage reports
      // it as a false 400 "Object not found". A getSession error means the
      // stored session is stale (expired JWT / invalid refresh token) — treat
      // as signed out, same as useAuth does.
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) return null;

      const { data, error } = await supabase.storage
        .from('files')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        // Only report while still authenticated — a session lost mid-request
        // produces the same "Object not found" for an RLS denial.
        const { data: { session: current }, error: currentError } = await supabase.auth.getSession();
        if (current && !currentError) {
          // Authenticated but file not found — log for regression detection
          captureMessage(`Storage object not found: ${storagePath}`, 'warning');
        }
        return null;
      }
      return data.signedUrl;
    },
    // Don't attempt storage requests without a valid session
    enabled: !!storagePath && hasSession,
    staleTime: 50 * 60 * 1000, // 50 minutes (buffer before 1-hour expiry)
    gcTime: 55 * 60 * 1000, // Keep in cache for 55 minutes
    retry: 1,
  });
}
